import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FiCheckCircle,
  FiCopy,
  FiHelpCircle,
  FiKey,
  FiPlus,
  FiRefreshCw,
  FiRotateCw,
  FiSave,
  FiSearch,
  FiShield,
  FiTrash2,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import axios from "../../api/axiosInstance";
import WhatsAppDeliveryNoteSetupGuide from "./WhatsAppDeliveryNoteSetupGuide";

const PROVIDER = "purchasing_whatsapp_delivery_notes";
const BUSINESS_UNITS_PER_PAGE = 10;
const normalizeSearch = (value) => String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const defaultSettings = {
  phone_number_id: "",
  display_phone_number: "",
  whatsapp_business_account_id: "",
  graph_api_version: "v23.0",
  auto_process: true,
  allow_images: true,
  allow_documents: true,
  authorized_senders: [],
};

const css = `
  .waDnPage {
    width: 100%;
    padding: 0 18px 18px;
  }

  .waDnShell {
    border-radius: 18px;
    border: 1px solid rgba(65, 210, 170, 0.26);
    background:
      linear-gradient(180deg, rgba(18, 24, 35, 0.96), rgba(8, 12, 22, 0.98)) padding-box,
      linear-gradient(135deg, rgba(65, 210, 170, 0.58), rgba(53, 168, 255, 0.28), rgba(255, 210, 105, 0.2)) border-box;
    box-shadow: 0 26px 78px rgba(0, 0, 0, 0.48);
    overflow: hidden;
  }

  .waDnHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    padding: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: linear-gradient(90deg, rgba(37, 211, 102, 0.1), rgba(53, 168, 255, 0.05));
  }

  .waDnKicker {
    color: rgba(65, 210, 170, 0.85);
    font-size: 0.74rem;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .waDnHeaderActions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .waDnTitle {
    margin: 0;
    color: rgba(244, 250, 255, 0.98);
    font-size: 1.42rem;
    font-weight: 900;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .waDnTitle svg {
    color: #25d366;
  }

  .waDnSubtitle {
    margin-top: 5px;
    color: rgba(230, 238, 248, 0.68);
    font-size: 0.86rem;
    font-weight: 700;
  }

  .waDnSummary {
    padding: 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .waDnSetupActions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 18px;
  }

  .waDnButton.waDnSetupCard {
    justify-content: flex-start;
    padding: 16px;
    text-align: left;
    border-color: rgba(65, 210, 170, 0.26);
    background: rgba(65, 210, 170, 0.055);
  }

  .waDnSetupCard > svg { flex-shrink: 0; font-size: 1.3rem; color: #88eace; }
  .waDnSetupCard strong, .waDnSetupCard small { display: block; }
  .waDnSetupCard small { margin-top: 5px; font-size: .78rem; font-weight: 500; color: rgba(230, 238, 248, .62); }
  .waDnListSearch { max-width: 480px; margin-bottom: 12px; }
  .waDnTable.waDnConfiguredList td { vertical-align: middle; padding: 7px 12px; }
  .waDnTable.waDnConfiguredList tbody th { vertical-align: middle; padding: 7px 12px; text-transform: none; background: transparent; }
  .waDnConfiguredList tr[aria-selected="true"] { background: rgba(65, 210, 170, .075); }
  .waDnButton.waDnEditButton { min-height: 32px; padding: 0 10px; font-size: .8rem; }
  .waDnPagination { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; }
  .waDnPaginationActions { display: flex; align-items: center; gap: 10px; }
  .waDnEditorHeader { align-items: flex-start; }
  .waDnEditorHeader .waDnButton { min-height: 32px; font-size: .78rem; }
  .waDnPageState { margin: 0 18px; }

  .waDnSummaryHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
  }

  .waDnSectionTitle {
    margin: 0;
    color: rgba(244, 250, 255, 0.94);
    font-size: 0.98rem;
    font-weight: 900;
  }

  .waDnMuted {
    color: rgba(230, 238, 248, 0.58);
    font-size: 0.78rem;
    font-weight: 800;
  }

  .waDnBuGrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 10px;
  }

  .waDnBuCard {
    min-height: 104px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.035);
    color: inherit;
    padding: 12px;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 9px;
  }

  .waDnBuCard:hover,
  .waDnBuCard--selected {
    border-color: rgba(65, 210, 170, 0.46);
    background: rgba(65, 210, 170, 0.08);
  }

  .waDnBuName {
    color: rgba(244, 250, 255, 0.96);
    font-size: 0.9rem;
    font-weight: 900;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .waDnBuFoot {
    margin-top: 4px;
    color: rgba(230, 238, 248, 0.56);
    font-size: 0.74rem;
    font-weight: 800;
  }

  .waDnBadgeRow {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .waDnBody {
    display: grid;
    grid-template-columns: minmax(380px, 0.95fr) minmax(0, 1.05fr);
    gap: 16px;
    padding: 18px;
  }

  .waDnPanel {
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.04);
    padding: 14px;
    min-width: 0;
  }

  .waDnGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .waDnWide {
    grid-column: 1 / -1;
  }

  .waDnField {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .waDnField > span,
  .waDnToggle span {
    color: rgba(230, 238, 248, 0.68);
    font-size: 0.78rem;
    font-weight: 850;
  }

  .waDnField input,
  .waDnField select {
    width: 100%;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    background: rgba(11, 18, 30, 0.76);
    color: rgba(244, 250, 255, 0.96);
    outline: 0;
    padding: 10px 11px;
    font: inherit;
    font-weight: 800;
  }

  .waDnField select {
    color-scheme: dark;
  }

  .waDnCopyBox {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }

  .waDnCopyBox input {
    font-size: 0.78rem;
  }

  .waDnToggle {
    min-height: 42px;
    border-radius: 11px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.035);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 11px;
  }

  .waDnToggle input {
    width: 16px;
    height: 16px;
  }

  .waDnBox {
    grid-column: 1 / -1;
    border-radius: 13px;
    border: 1px solid rgba(65, 210, 170, 0.16);
    background: rgba(65, 210, 170, 0.055);
    padding: 12px;
  }

  .waDnBoxHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 11px;
  }

  .waDnBoxTitle {
    margin: 0;
    color: rgba(244, 250, 255, 0.95);
    font-size: 0.96rem;
    font-weight: 900;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .waDnHint {
    margin: 8px 0 0;
    color: rgba(230, 238, 248, 0.62);
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .waDnActions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }

  .waDnButton {
    min-height: 40px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    background: rgba(255, 255, 255, 0.055);
    color: rgba(244, 250, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 13px;
    font: inherit;
    font-weight: 850;
    cursor: pointer;
  }

  .waDnButton--primary {
    color: #07130e;
    background: #41d2aa;
    border-color: rgba(65, 210, 170, 0.6);
  }

  .waDnButton--danger {
    color: #ffdada;
    border-color: rgba(255, 93, 93, 0.32);
    background: rgba(255, 93, 93, 0.11);
  }

  .waDnButton:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .waDnButton:focus-visible,
  .waDnBuCard:focus-visible {
    outline: 2px solid #88eace;
    outline-offset: 3px;
  }

  .waDnBuPicker .modal-content {
    color: #eaf3fa;
    background: #101925;
    border: 1px solid rgba(65, 210, 170, 0.35);
    border-radius: 18px;
  }

  .waDnBuPicker .modal-header {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .waDnBuPicker .waDnBuGrid {
    margin-top: 14px;
    max-height: 55vh;
    overflow-y: auto;
    padding: 4px;
  }

  .waDnBuPicker .waDnBuCard {
    min-height: 78px;
  }

  .waDnCopySummary {
    margin-top: 18px;
    padding: 14px;
    border: 1px solid rgba(65, 210, 170, 0.26);
    border-radius: 12px;
    background: rgba(65, 210, 170, 0.055);
    overflow-wrap: anywhere;
  }

  .waDnCopySummary h3 {
    margin: 0 0 10px;
    font-size: 1rem;
  }

  .waDnCopySummary ul {
    margin: 10px 0;
    padding-left: 22px;
    font-size: 0.85rem;
    line-height: 1.6;
  }

  .waDnCopyWarning {
    color: #ffe7ad;
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .waDnBuPicker .modal-footer {
    border-color: rgba(255, 255, 255, 0.1);
    gap: 8px;
  }

  .waDnBadge {
    min-height: 26px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.11);
    padding: 4px 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(244, 250, 255, 0.9);
    background: rgba(255, 255, 255, 0.045);
    font-size: 0.75rem;
    font-weight: 900;
    text-transform: capitalize;
    white-space: nowrap;
  }

  .waDnBadge--active,
  .waDnBadge--configured,
  .waDnBadge--import_created,
  .waDnBadge--valid {
    color: #cbfff0;
    border-color: rgba(65, 210, 170, 0.34);
    background: rgba(65, 210, 170, 0.13);
  }

  .waDnBadge--draft,
  .waDnBadge--missing,
  .waDnBadge--not_configured,
  .waDnBadge--duplicate,
  .waDnBadge--ignored {
    color: #ffe7ad;
    border-color: rgba(255, 210, 105, 0.34);
    background: rgba(255, 210, 105, 0.12);
  }

  .waDnBadge--disabled,
  .waDnBadge--invalid,
  .waDnBadge--failed,
  .waDnBadge--error {
    color: #ffd6d6;
    border-color: rgba(255, 93, 93, 0.32);
    background: rgba(255, 93, 93, 0.1);
  }

  .waDnState {
    border-radius: 11px;
    padding: 10px 11px;
    margin-bottom: 12px;
    font-weight: 800;
    line-height: 1.35;
  }

  .waDnState--info {
    color: #d9fff5;
    border: 1px solid rgba(65, 210, 170, 0.22);
    background: rgba(65, 210, 170, 0.075);
  }

  .waDnState--error {
    color: #ffd6d6;
    border: 1px solid rgba(255, 93, 93, 0.32);
    background: rgba(130, 26, 34, 0.24);
  }

  .waDnTableShell {
    overflow: auto;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.09);
  }

  .waDnTable {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
  }

  .waDnTable th,
  .waDnTable td {
    padding: 11px 12px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    vertical-align: top;
  }

  .waDnTable th {
    color: rgba(230, 238, 248, 0.62);
    font-size: 0.73rem;
    text-transform: uppercase;
    font-weight: 900;
    background: rgba(255, 255, 255, 0.035);
  }

  .waDnTable td {
    color: rgba(230, 238, 248, 0.88);
    font-size: 0.84rem;
  }

  .waDnMono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    overflow-wrap: anywhere;
  }

  .waDnEmpty {
    min-height: 120px;
    display: grid;
    place-items: center;
    color: rgba(230, 238, 248, 0.58);
    font-weight: 850;
    text-align: center;
    padding: 14px;
  }

  @media (max-width: 640px) {
    .waDnSetupActions { grid-template-columns: 1fr; }
    .waDnPagination { align-items: flex-start; flex-direction: column; }
  }

  @media (max-width: 1080px) {
    .waDnBody,
    .waDnHeader {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .waDnGrid {
      grid-template-columns: 1fr;
    }
  }
`;

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (err?.response?.status === 409) {
    const detail = typeof data?.error === "string" ? data.error : data?.message;
    return `${detail || "This receiving number and sender combination is already assigned to another Business Unit."} Review the authorized sender lists for this receiving number before saving again.`;
  }
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (typeof data === "string" && data.trim()) return data.trim().slice(0, 220);
  if (err?.response?.status) return `${fallback} (HTTP ${err.response.status})`;
  if (err?.message) return `${fallback}: ${err.message}`;
  return fallback;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function eventLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.replace(/_/g, " ");
}

function buildSettingsPayload(settings) {
  // Sender authorization belongs to the BU Settings endpoint. Omitting it here
  // lets the backend preserve the latest list and any learned sender identity.
  return {
    phone_number_id: String(settings.phone_number_id || "").trim(),
    display_phone_number: String(settings.display_phone_number || "").trim(),
    whatsapp_business_account_id: String(settings.whatsapp_business_account_id || "").trim(),
    graph_api_version: String(settings.graph_api_version || "v23.0").trim(),
    auto_process: Boolean(settings.auto_process),
    allow_images: Boolean(settings.allow_images),
    allow_documents: Boolean(settings.allow_documents),
  };
}

function activeSenderCount(settings) {
  return Array.isArray(settings?.authorized_senders)
    ? settings.authorized_senders.filter((sender) => sender.active !== false).length
    : 0;
}

export default function WhatsAppDeliveryNoteIntegrations() {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [logSnapshot, setLogSnapshot] = useState(null);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState("");
  const [status, setStatus] = useState("draft");
  const [settings, setSettings] = useState(defaultSettings);
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deletingToken, setDeletingToken] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [showBusinessUnitPicker, setShowBusinessUnitPicker] = useState(false);
  const [pickerIntent, setPickerIntent] = useState("new");
  const [editorMode, setEditorMode] = useState(null);
  const [businessUnitSearch, setBusinessUnitSearch] = useState("");
  const [configuredSearch, setConfiguredSearch] = useState("");
  const [configuredPage, setConfiguredPage] = useState(1);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copySourceBusinessUnitId, setCopySourceBusinessUnitId] = useState("");
  const [copySearch, setCopySearch] = useState("");
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState("");
  const copyLock = useRef(false);
  const editorRef = useRef(null);
  const busy = saving || tokenSaving || testing || deletingToken || copying;
  const activeSenders = activeSenderCount(settings);
  const filteredBusinessUnits = useMemo(() => {
    const configuredIds = new Set(integrations.map((integration) => String(integration.business_unit_id)));
    const query = normalizeSearch(businessUnitSearch.trim());
    return businessUnits.filter((unit) =>
      (pickerIntent === "copy" || (pickerIntent === "new" ? !configuredIds.has(String(unit.id)) : configuredIds.has(String(unit.id)))) &&
      normalizeSearch(`${unit.name} ${unit.id}`).includes(query));
  }, [businessUnits, businessUnitSearch, integrations, pickerIntent]);

  const businessUnitNameById = useMemo(() => {
    const map = new Map();
    businessUnits.forEach((businessUnit) => {
      map.set(String(businessUnit.id), businessUnit.name);
    });
    return map;
  }, [businessUnits]);

  const selectedIntegration = useMemo(
    () =>
      integrations.find(
        (integration) => String(integration.business_unit_id) === String(selectedBusinessUnitId)
      ) || null,
    [integrations, selectedBusinessUnitId]
  );

  // Hide rows from a previous selection before the next effect starts, including
  // a refreshed integration with the same ID but a different object snapshot.
  const logs = logSnapshot?.integration === selectedIntegration &&
    logSnapshot?.businessUnitId === selectedBusinessUnitId ? logSnapshot.rows : [];

  const configuredIntegrations = useMemo(
    () =>
      [...integrations].sort((a, b) => {
        const aName = a.business_unit?.name || businessUnitNameById.get(String(a.business_unit_id)) || "";
        const bName = b.business_unit?.name || businessUnitNameById.get(String(b.business_unit_id)) || "";
        return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: "base" });
      }),
    [businessUnitNameById, integrations]
  );

  const selectedBusinessUnitName =
    businessUnitNameById.get(String(selectedBusinessUnitId)) ||
    selectedIntegration?.business_unit?.name ||
    "";

  const copySources = useMemo(() => configuredIntegrations.filter((integration) =>
    String(integration.business_unit_id) !== String(selectedBusinessUnitId) &&
    String(integration.settings?.phone_number_id || "").trim()), [configuredIntegrations, selectedBusinessUnitId]);
  const selectedCopySource = copySources.find((integration) => String(integration.business_unit_id) === copySourceBusinessUnitId);
  const integrationBusinessUnitName = (integration) => integration.business_unit?.name ||
    businessUnitNameById.get(String(integration.business_unit_id)) || `Business Unit ${integration.business_unit_id}`;
  const filteredConfiguredIntegrations = configuredIntegrations.filter((integration) => normalizeSearch([
    integrationBusinessUnitName(integration), integration.business_unit_id,
    integration.settings?.display_phone_number, integration.settings?.phone_number_id,
  ].join(" ")).includes(normalizeSearch(configuredSearch.trim())));
  const configuredPageCount = Math.max(1, Math.ceil(filteredConfiguredIntegrations.length / BUSINESS_UNITS_PER_PAGE));
  const visibleConfiguredPage = Math.min(configuredPage, configuredPageCount);
  const configuredOffset = (visibleConfiguredPage - 1) * BUSINESS_UNITS_PER_PAGE;
  const visibleConfiguredIntegrations = filteredConfiguredIntegrations.slice(configuredOffset, configuredOffset + BUSINESS_UNITS_PER_PAGE);
  const visibleCopySources = copySources.filter((integration) => normalizeSearch([
    integrationBusinessUnitName(integration), integration.business_unit_id,
    integration.settings?.display_phone_number, integration.settings?.phone_number_id,
  ].join(" ")).includes(normalizeSearch(copySearch.trim())));

  const loadData = useCallback(async ({ preserveError = false } = {}) => {
    setLoading(true);
    if (!preserveError) setError("");
    try {
      const [businessUnitsResponse, integrationsResponse] = await Promise.all([
        axios.get("/api/business-units/all"),
        axios.get("/api/admin/crm-provider-integrations", { params: { provider: PROVIDER } }),
      ]);

      const nextBusinessUnits = Array.isArray(businessUnitsResponse.data)
        ? businessUnitsResponse.data
        : [];
      const nextIntegrations = Array.isArray(integrationsResponse.data?.rows)
        ? integrationsResponse.data.rows
        : [];

      setBusinessUnits(nextBusinessUnits);
      setIntegrations(nextIntegrations);
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes integration load error:", err);
      const detail = getApiErrorMessage(err, "Could not load WhatsApp delivery note integrations.");
      setError((current) => preserveError
        ? [current, `Could not refresh integration details: ${detail}`].filter(Boolean).join(" ")
        : detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (editorMode) editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [editorMode, selectedBusinessUnitId]);

  useEffect(() => {
    if (selectedIntegration) {
      setStatus(selectedIntegration.status || "draft");
      setSettings({ ...defaultSettings, ...(selectedIntegration.settings || {}) });
    } else {
      setStatus("draft");
      setSettings(defaultSettings);
    }
    setAccessToken("");
  }, [selectedIntegration, selectedBusinessUnitId]);

  useEffect(() => {
    setLogSnapshot(null);
    if (!selectedIntegration?.id) return undefined;

    let current = true;
    const controller = new AbortController();
    const loadLogs = async () => {
      try {
        const response = await axios.get(`/api/admin/crm-provider-integrations/${selectedIntegration.id}/webhook-logs`, {
          params: { limit: 30 },
          signal: controller.signal,
        });
        if (!current) return;
        setLogSnapshot({
          integration: selectedIntegration,
          businessUnitId: selectedBusinessUnitId,
          rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
        });
      } catch (err) {
        if (!current) return;
        console.error("[Admin] WhatsApp delivery note logs load error:", err);
        setLogSnapshot(null);
      }
    };
    loadLogs();
    return () => {
      current = false;
      controller.abort();
    };
  }, [selectedIntegration, selectedBusinessUnitId]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setMessage("");
  };

  const selectBusinessUnit = (businessUnitId, intent = pickerIntent) => {
    if (busy || copyLock.current) return;
    const existing = integrations.some((integration) => String(integration.business_unit_id) === String(businessUnitId));
    if (intent === "new" && existing) return;
    setSelectedBusinessUnitId(String(businessUnitId));
    setEditorMode(existing ? "edit" : "new");
    setShowBusinessUnitPicker(false);
    setBusinessUnitSearch("");
    setError("");
    setMessage("");
    if (intent === "copy") {
      setCopySourceBusinessUnitId(""); setCopySearch(""); setCopyError("");
      setShowCopyDialog(true);
    }
  };

  const openBusinessUnitPicker = (intent) => {
    if (busy || loading) return;
    setPickerIntent(intent);
    setBusinessUnitSearch("");
    setShowBusinessUnitPicker(true);
  };

  const closeEditor = () => {
    if (busy || copyLock.current) return;
    setEditorMode(null); setSelectedBusinessUnitId(""); setError(""); setMessage("");
  };

  const closeCopyDialog = () => {
    if (copyLock.current) return;
    setShowCopyDialog(false); setCopySourceBusinessUnitId(""); setCopySearch(""); setCopyError("");
  };

  const copyIntegration = async () => {
    if (copyLock.current || busy || loading || !selectedCopySource || !selectedBusinessUnitId) return;
    const targetBusinessUnitId = selectedBusinessUnitId;
    const sourceName = integrationBusinessUnitName(selectedCopySource);
    const targetName = selectedBusinessUnitName || `Business Unit ${targetBusinessUnitId}`;
    copyLock.current = true; setCopying(true); setCopyError(""); setError(""); setMessage("");
    try {
      // Only identities travel from the browser. The server copies saved settings
      // and encrypted tokens while preserving the destination's sender list.
      const response = await axios.post("/api/admin/crm-provider-integrations/whatsapp-delivery-notes/copy", {
        source_business_unit_id: Number(selectedCopySource.business_unit_id),
        target_business_unit_id: Number(targetBusinessUnitId),
      });
      setIntegrations((prev) => [...prev.filter((integration) =>
        String(integration.business_unit_id) !== String(targetBusinessUnitId) && integration.id !== response.data.id), response.data]);
      setSelectedBusinessUnitId(String(targetBusinessUnitId));
      setEditorMode("edit");
      setAccessToken("");
      setShowCopyDialog(false); setCopySourceBusinessUnitId(""); setCopySearch("");
      setMessage(`Configuración y tokens guardados de ${sourceName} copiados a ${targetName}. Se conservan las autorizaciones de remitentes del destino.`);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "WHATSAPP_SENDER_ROUTE_CONFLICT") {
        setCopyError("No se ha copiado nada: un remitente del destino ya está asignado a otra BU para este receptor. Revisa sus autorizaciones antes de copiar.");
        return;
      }
      const detail = typeof data?.error === "string" ? data.error : typeof data?.message === "string" ? data.message : "";
      const prefix = err?.response?.status === 403 ? "No tienes permiso para copiar esta configuración."
        : err?.response?.status === 409 ? "No se puede copiar porque hay un conflicto en la configuración."
        : "No se ha podido copiar la configuración y los tokens. Vuelve a intentarlo.";
      setCopyError(`${prefix}${detail ? ` ${detail}` : ""}`);
    } finally {
      copyLock.current = false; setCopying(false);
    }
  };

  const saveIntegration = async () => {
    if (!selectedBusinessUnitId) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        business_unit_id: selectedBusinessUnitId,
        name: "WhatsApp Delivery Notes",
        status,
        settings: buildSettingsPayload(settings),
      };

      const response = selectedIntegration?.id
        ? await axios.patch(`/api/admin/crm-provider-integrations/${selectedIntegration.id}`, payload)
        : await axios.post("/api/admin/crm-provider-integrations/whatsapp-delivery-notes", payload);

      setIntegrations((prev) => [
        ...prev.filter((item) => item.id !== response.data.id),
        response.data,
      ]);
      setMessage("WhatsApp delivery notes settings saved.");
      setEditorMode("edit");
      await loadData();
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes save error:", err);
      setError(getApiErrorMessage(err, "Could not save WhatsApp delivery notes settings."));
    } finally {
      setSaving(false);
    }
  };

  const saveAccessToken = async () => {
    if (!selectedIntegration?.id) {
      setError("Prepare this Business Unit before saving the Meta access token.");
      return;
    }
    if (!accessToken.trim()) {
      setError("Meta access token is required.");
      return;
    }

    setTokenSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`,
        {
          access_token: accessToken,
          settings: buildSettingsPayload(settings),
        }
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setSettings({ ...defaultSettings, ...(response.data.settings || {}) });
      setAccessToken("");
      setMessage("Meta access token saved.");
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes token save error:", err);
      setError(getApiErrorMessage(err, "Could not save Meta access token."));
    } finally {
      setTokenSaving(false);
    }
  };

  const deleteAccessToken = async () => {
    if (!selectedIntegration?.id) return;
    const confirmed = window.confirm("Remove saved Meta access token for this Business Unit?");
    if (!confirmed) return;

    setDeletingToken(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.delete(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setMessage("Meta access token removed.");
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes token delete error:", err);
      setError(getApiErrorMessage(err, "Could not remove Meta access token."));
    } finally {
      setDeletingToken(false);
    }
  };

  const testConnection = async () => {
    if (!selectedIntegration?.id) return;

    setTesting(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/test-connection`,
        { settings: buildSettingsPayload(settings) }
      );
      const phone = response.data?.display_phone_number || response.data?.phone_number_id || "phone number";
      const name = response.data?.verified_name ? ` (${response.data.verified_name})` : "";
      setMessage(`WhatsApp Cloud API connection OK for ${phone}${name}.`);
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes test error:", err);
      setError(getApiErrorMessage(err, "Could not test WhatsApp Cloud API connection."));
    } finally {
      // Refresh persisted credential status without erasing the test result.
      await loadData({ preserveError: true });
      setTesting(false);
    }
  };

  const rotateWebhookToken = async () => {
    if (!selectedIntegration?.id) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/rotate-webhook-token`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setMessage("Verify token rotated.");
    } catch (err) {
      console.error("[Admin] WhatsApp delivery notes token rotate error:", err);
      setError(getApiErrorMessage(err, "Could not rotate verify token."));
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setMessage(`${label} copied.`);
    } catch (_err) {
      setMessage(`${label} is ready to copy manually.`);
    }
  };

  const configurationPanel = (
        <div className="waDnBody" data-editor-mode={editorMode} ref={editorRef}>
          <section className="waDnPanel">
            <div className="waDnSummaryHeader waDnEditorHeader">
              <div><h3 className="waDnSectionTitle">{editorMode === "new" ? "Nueva configuración" : "Editar configuración"}</h3>
                <p className="waDnHint">{selectedBusinessUnitName}</p></div>
              <button type="button" className="waDnButton" disabled={busy} onClick={closeEditor}>Cerrar configuración</button>
            </div>

            {error ? <div className="waDnState waDnState--error" role="alert">{error}</div> : null}
            {message ? <div className="waDnState waDnState--info" role="status">{message}</div> : null}

            <div className="waDnGrid">
              <div className="waDnField waDnWide">
                <span id="wa-dn-business-unit-label">Business Unit</span>
                <button
                  type="button"
                  className="waDnButton"
                  aria-labelledby="wa-dn-business-unit-label wa-dn-selected-business-unit"
                  aria-haspopup="dialog"
                  disabled={loading || busy}
                  onClick={() => openBusinessUnitPicker(editorMode)}
                >
                  <FiSearch aria-hidden="true" />
                  <span id="wa-dn-selected-business-unit">{selectedBusinessUnitName || "Choose Business Unit"}</span>
                </button>
              </div>

              <label className="waDnField">
                <span>Status</span>
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>

              <label className="waDnField">
                <span>Graph API version</span>
                <input
                  value={settings.graph_api_version || "v23.0"}
                  onChange={(event) => updateSetting("graph_api_version", event.target.value)}
                  placeholder="v23.0"
                  autoComplete="off"
                />
              </label>

              <label className="waDnField">
                <span>Receiving phone number (display)</span>
                <input
                  value={settings.display_phone_number || ""}
                  onChange={(event) => updateSetting("display_phone_number", event.target.value)}
                  placeholder="+34600000000"
                  autoComplete="off"
                />
              </label>

              <label className="waDnField">
                <span>Phone number ID (receiver)</span>
                <input
                  value={settings.phone_number_id || ""}
                  onChange={(event) => updateSetting("phone_number_id", event.target.value)}
                  placeholder="Meta phone number ID"
                  autoComplete="off"
                />
              </label>

              <p className="waDnHint waDnWide">
                The same receiving number can serve several Business Units. Each receiving number + sender
                combination belongs to one BU. A sender can serve another BU through a different receiving number.
              </p>

              <label className="waDnField waDnWide">
                <span>WhatsApp Business Account ID</span>
                <input
                  value={settings.whatsapp_business_account_id || ""}
                  onChange={(event) => updateSetting("whatsapp_business_account_id", event.target.value)}
                  placeholder="Optional WABA ID"
                  autoComplete="off"
                />
              </label>

              <section className="waDnBox" aria-labelledby="wa-dn-senders-title">
                <div className="waDnBoxHeader">
                  <h4 className="waDnBoxTitle" id="wa-dn-senders-title"><FiShield aria-hidden="true" />Authorized senders</h4>
                  <span className={`waDnBadge waDnBadge--${activeSenders ? "active" : "missing"}`}>{activeSenders} active</span>
                </div>
                <p className="waDnHint">Manage authorized senders in the Business Unit's Purchasing → Settings → WhatsApp senders.
                  This admin panel configures the receiving number and Meta connection.</p>
                {!activeSenders && <p className="waDnHint">No active senders are saved. Incoming documents for this BU are discarded before download or OCR until a sender is authorized in its Settings.</p>}
              </section>

              <label className="waDnToggle waDnWide">
                <input
                  type="checkbox"
                  checked={Boolean(settings.auto_process)}
                  onChange={(event) => updateSetting("auto_process", event.target.checked)}
                />
                <span>Start OCR automatically after each authorized WhatsApp document</span>
              </label>

              <label className="waDnToggle">
                <input
                  type="checkbox"
                  checked={Boolean(settings.allow_images)}
                  onChange={(event) => updateSetting("allow_images", event.target.checked)}
                />
                <span>Accept images</span>
              </label>

              <label className="waDnToggle">
                <input
                  type="checkbox"
                  checked={Boolean(settings.allow_documents)}
                  onChange={(event) => updateSetting("allow_documents", event.target.checked)}
                />
                <span>Accept PDFs</span>
              </label>

              <div className="waDnBox">
                <div className="waDnBoxHeader">
                  <h4 className="waDnBoxTitle">
                    <FiShield aria-hidden="true" />
                    <span>Meta webhook</span>
                  </h4>
                  <span className={`waDnBadge waDnBadge--${selectedIntegration?.status || "draft"}`}>
                    {selectedIntegration?.status || "not prepared"}
                  </span>
                </div>

                <label className="waDnField waDnWide">
                  <span>Callback URL</span>
                  <div className="waDnCopyBox">
                    <input value={selectedIntegration?.webhook_url || "Prepare integration first"} readOnly />
                    <button
                      type="button"
                      className="waDnButton"
                      onClick={() => copyText(selectedIntegration?.webhook_url, "Callback URL")}
                      disabled={!selectedIntegration?.webhook_url}
                      title="Copy callback URL"
                    >
                      <FiCopy aria-hidden="true" />
                    </button>
                  </div>
                </label>

                <label className="waDnField waDnWide" style={{ marginTop: 10 }}>
                  <span>Verify token</span>
                  <div className="waDnCopyBox">
                    <input value={selectedIntegration?.webhook_token || "Prepare integration first"} readOnly />
                    <button
                      type="button"
                      className="waDnButton"
                      onClick={() => copyText(selectedIntegration?.webhook_token, "Verify token")}
                      disabled={!selectedIntegration?.webhook_token}
                      title="Copy verify token"
                    >
                      <FiCopy aria-hidden="true" />
                    </button>
                  </div>
                </label>

                <div className="waDnActions">
                  <button
                    type="button"
                    className="waDnButton"
                    onClick={rotateWebhookToken}
                    disabled={busy || !selectedIntegration?.id}
                  >
                    <FiRotateCw aria-hidden="true" />
                    <span>Rotate verify token</span>
                  </button>
                </div>
              </div>

              <div className="waDnBox">
                <div className="waDnBoxHeader">
                  <h4 className="waDnBoxTitle">
                    <FiKey aria-hidden="true" />
                    <span>Meta access token</span>
                  </h4>
                  <span
                    className={`waDnBadge waDnBadge--${
                      selectedIntegration?.credentials_status || "missing"
                    }`}
                  >
                    {selectedIntegration?.credentials_status || "missing"}
                  </span>
                </div>

                <label className="waDnField waDnWide">
                  <span>Access token</span>
                  <input
                    type="password"
                    value={accessToken}
                    onChange={(event) => setAccessToken(event.target.value)}
                    placeholder={
                      selectedIntegration?.has_credentials
                        ? "Saved token is hidden"
                        : "Paste permanent Meta access token"
                    }
                    autoComplete="new-password"
                    disabled={!selectedIntegration?.id || busy}
                  />
                </label>

                <div className="waDnActions">
                  <button
                    type="button"
                    className="waDnButton waDnButton--primary"
                    onClick={saveAccessToken}
                    disabled={busy || !selectedIntegration?.id || !accessToken.trim()}
                  >
                    <FiSave aria-hidden="true" />
                    <span>{tokenSaving ? "Saving" : "Save token"}</span>
                  </button>
                  <button
                    type="button"
                    className="waDnButton"
                    onClick={testConnection}
                    disabled={busy || !selectedIntegration?.has_credentials}
                  >
                    <FiCheckCircle aria-hidden="true" />
                    <span>{testing ? "Testing" : "Test connection"}</span>
                  </button>
                  <button
                    type="button"
                    className="waDnButton waDnButton--danger"
                    onClick={deleteAccessToken}
                    disabled={busy || !selectedIntegration?.has_credentials}
                  >
                    <FiTrash2 aria-hidden="true" />
                    <span>{deletingToken ? "Removing" : "Remove token"}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="waDnActions">
              <button
                type="button"
                className="waDnButton waDnButton--primary"
                onClick={saveIntegration}
                disabled={busy || loading || !selectedBusinessUnitId}
              >
                <FiSave aria-hidden="true" />
                <span>{selectedIntegration ? "Save settings" : "Prepare WhatsApp"}</span>
              </button>
              {selectedBusinessUnitName ? (
                <span className="waDnMuted" style={{ alignSelf: "center" }}>
                  {selectedBusinessUnitName}
                </span>
              ) : null}
            </div>
          </section>

          <section className="waDnPanel">
            <div className="waDnSummaryHeader">
              <h3 className="waDnSectionTitle">Webhook events</h3>
              <div className="waDnMuted">{logs.length} latest</div>
            </div>

            <div className="waDnTableShell">
              {logs.length ? (
                <table className="waDnTable">
                  <thead>
                    <tr>
                      <th>Received</th>
                      <th>Type</th>
                      <th>Sender</th>
                      <th>Message ID</th>
                      <th>Status</th>
                      <th>Signature</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDateTime(log.created_at)}</td>
                        <td>{eventLabel(log.event_type)}</td>
                        <td className="waDnMono">{log.external_conversation_id || "-"}</td>
                        <td className="waDnMono">{log.external_event_id || "-"}</td>
                        <td>
                          <span className={`waDnBadge waDnBadge--${log.status || "received"}`}>
                            {eventLabel(log.status || "received")}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`waDnBadge waDnBadge--${
                              log.signature_status || "not_configured"
                            }`}
                          >
                            {eventLabel(log.signature_status || "not_configured")}
                          </span>
                        </td>
                        <td>{log.error_message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="waDnEmpty">
                  {selectedIntegration ? "No WhatsApp webhook events yet." : "Prepare an integration first."}
                </div>
              )}
            </div>
          </section>
        </div>
  );

  return (
    <div className="waDnPage">
      <style>{css}</style>
      <div className="waDnShell">
        <div className="waDnHeader">
          <div>
            <div className="waDnKicker">Purchasing integrations</div>
            <h2 className="waDnTitle">
              <FaWhatsapp aria-hidden="true" />
              <span>WhatsApp Delivery Notes</span>
            </h2>
            <div className="waDnSubtitle">
              Route delivery note scans by receiving number and authorized sender.
            </div>
          </div>
          <div className="waDnHeaderActions">
            <button
              type="button"
              className="waDnButton waDnButton--primary"
              onClick={() => setShowSetupGuide(true)}
              aria-haspopup="dialog"
              disabled={busy}
            >
              <FiHelpCircle aria-hidden="true" />
              <span>Cómo configurar números y remitentes</span>
            </button>
            <button type="button" className="waDnButton" onClick={loadData} disabled={loading || busy}>
              <FiRefreshCw aria-hidden="true" />
              <span>{loading ? "Loading" : "Refresh"}</span>
            </button>
          </div>
        </div>

        <section className="waDnSetupActions" aria-label="Configurar unidades de negocio">
          <button type="button" className="waDnButton waDnSetupCard" disabled={loading || busy}
            onClick={() => openBusinessUnitPicker("new")} aria-haspopup="dialog" aria-label="Configurar nueva BU">
            <FiPlus aria-hidden="true" /><span><strong>Configurar nueva BU</strong>
              <small>Elige una unidad sin configurar y prepara su conexión.</small></span>
          </button>
          <button type="button" className="waDnButton waDnSetupCard" disabled={loading || busy}
            onClick={() => openBusinessUnitPicker("copy")} aria-haspopup="dialog" aria-label="Copiar configuración a otra BU">
            <FiCopy aria-hidden="true" /><span><strong>Copiar configuración a otra BU</strong>
              <small>Selecciona primero el destino y después la configuración de origen.</small></span>
          </button>
        </section>
        {!editorMode && error && <div className="waDnState waDnState--error waDnPageState" role="alert">{error}</div>}
        {editorMode === "new" && configurationPanel}

        <section className="waDnSummary" aria-labelledby="wa-dn-configured-title">
          <div className="waDnSummaryHeader">
            <h3 id="wa-dn-configured-title" className="waDnSectionTitle">Unidades configuradas</h3>
            <span className="waDnMuted">{configuredIntegrations.length} BUs</span>
          </div>
          <label className="waDnField waDnListSearch"><span>Buscar por BU o teléfono</span>
            <input type="search" value={configuredSearch} placeholder="Nombre, ID o número receptor"
              onChange={(event) => { setConfiguredSearch(event.target.value); setConfiguredPage(1); }} />
          </label>
          <div className="waDnTableShell">
            <table className="waDnTable waDnConfiguredList" aria-labelledby="wa-dn-configured-title">
              <thead><tr><th scope="col">Business Unit</th><th scope="col">Número receptor</th>
                <th scope="col">Estado</th><th scope="col">Credenciales</th><th scope="col">Remitentes activos</th><th scope="col">Acciones</th></tr></thead>
              <tbody>{visibleConfiguredIntegrations.map((integration) => (
                <tr key={integration.id} aria-selected={String(integration.business_unit_id) === selectedBusinessUnitId}>
                  <th scope="row"><span className="waDnBuName">{integrationBusinessUnitName(integration)}</span></th>
                  <td>{integration.settings?.display_phone_number || integration.settings?.phone_number_id || "Sin número"}</td>
                  <td><span className={`waDnBadge waDnBadge--${integration.status || "draft"}`}>{integration.status || "draft"}</span></td>
                  <td><span className={`waDnBadge waDnBadge--${integration.credentials_status || "missing"}`}>{integration.credentials_status || "missing"}</span></td>
                  <td>{activeSenderCount(integration.settings)}</td>
                  <td><button type="button" className="waDnButton waDnEditButton"
                    aria-label={`Editar ${integrationBusinessUnitName(integration)}`} disabled={busy || loading}
                    onClick={() => selectBusinessUnit(integration.business_unit_id, "edit")}>Editar</button></td>
                </tr>
              ))}</tbody>
            </table>
            {!visibleConfiguredIntegrations.length && <div className="waDnEmpty" role="status">{loading ? "Cargando unidades…" :
              configuredIntegrations.length ? "No hay unidades que coincidan con la búsqueda." : "Todavía no hay unidades configuradas."}</div>}
          </div>
          <div className="waDnPagination">
            <span className="waDnMuted" role="status">{filteredConfiguredIntegrations.length ? configuredOffset + 1 : 0}–{Math.min(configuredOffset + BUSINESS_UNITS_PER_PAGE, filteredConfiguredIntegrations.length)} de {filteredConfiguredIntegrations.length} BUs</span>
            <nav className="waDnPaginationActions" aria-label="Páginas de unidades configuradas">
              <button type="button" className="waDnButton waDnEditButton" disabled={visibleConfiguredPage <= 1}
                onClick={() => setConfiguredPage(visibleConfiguredPage - 1)}>Anterior</button>
              <span className="waDnMuted">{visibleConfiguredPage} / {configuredPageCount}</span>
              <button type="button" className="waDnButton waDnEditButton" disabled={visibleConfiguredPage >= configuredPageCount}
                onClick={() => setConfiguredPage(visibleConfiguredPage + 1)}>Siguiente</button>
            </nav>
          </div>
        </section>

        {editorMode === "edit" && configurationPanel}
      </div>
      <Modal show={showBusinessUnitPicker} onHide={() => { setShowBusinessUnitPicker(false); setBusinessUnitSearch(""); }}
        centered scrollable className="waDnBuPicker" aria-labelledby="wa-dn-bu-picker-title">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title id="wa-dn-bu-picker-title">{pickerIntent === "new" ? "Configurar nueva BU" : pickerIntent === "copy" ? "Elegir BU de destino" : "Elegir unidad configurada"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pickerIntent === "new" && <p className="waDnHint">Solo se muestran unidades que todavía no tienen una configuración.</p>}
          {pickerIntent === "copy" && <p className="waDnHint">Elige la unidad que recibirá la configuración. Después seleccionarás el origen.</p>}
          <label className="waDnField"><span>Search by name or ID</span>
            <input type="search" autoFocus value={businessUnitSearch}
              onChange={(event) => setBusinessUnitSearch(event.target.value)} placeholder="Business Unit name or ID" />
          </label>
          <div className="waDnBuGrid">
            {filteredBusinessUnits.slice(0, 60).map((unit) => (
              <button type="button" key={unit.id} className={`waDnBuCard${String(unit.id) === selectedBusinessUnitId ? " waDnBuCard--selected" : ""}`}
                aria-pressed={String(unit.id) === selectedBusinessUnitId} onClick={() => selectBusinessUnit(unit.id)}>
                <strong className="waDnBuName">{unit.name}</strong><span className="waDnBuFoot">BU {unit.id}</span>
              </button>
            ))}
          </div>
          {!filteredBusinessUnits.length && <p className="waDnHint" role="status">{pickerIntent === "new" && !businessUnitSearch ? "Todas las unidades disponibles ya están configuradas." : "No Business Units match your search."}</p>}
          {filteredBusinessUnits.length > 60 && <p className="waDnHint">Showing 60 of {filteredBusinessUnits.length}. Refine your search.</p>}
        </Modal.Body>
      </Modal>
      <Modal show={showCopyDialog} onHide={closeCopyDialog} centered scrollable size="lg" className="waDnBuPicker"
        lang="es" keyboard={!copying} backdrop={copying ? "static" : true}
        aria-labelledby="wa-dn-copy-title" aria-describedby="wa-dn-copy-description">
        <Modal.Header closeButton={!copying} closeVariant="white">
          <Modal.Title id="wa-dn-copy-title">Copiar configuración de otra BU</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={copying}>
          <p id="wa-dn-copy-description">Destino: <strong>{selectedBusinessUnitName || `Business Unit ${selectedBusinessUnitId}`}</strong>.
            Elige la BU de origen. Se copiará su configuración guardada; los cambios sin guardar no se incluyen.</p>
          {copyError && <div className="waDnState waDnState--error" role="alert">{copyError}</div>}
          <label className="waDnField"><span>Buscar BU de origen por nombre, ID o receptor</span>
            <input type="search" autoFocus value={copySearch} disabled={copying}
              onChange={(event) => setCopySearch(event.target.value)} placeholder="Nombre, ID o teléfono receptor" />
          </label>
          <div className="waDnBuGrid">
            {visibleCopySources.slice(0, 60).map((integration) => <button type="button" key={integration.id}
              className={`waDnBuCard${String(integration.business_unit_id) === copySourceBusinessUnitId ? " waDnBuCard--selected" : ""}`}
              aria-pressed={String(integration.business_unit_id) === copySourceBusinessUnitId} disabled={copying}
              onClick={() => { setCopySourceBusinessUnitId(String(integration.business_unit_id)); setCopyError(""); }}>
              <strong className="waDnBuName">{integrationBusinessUnitName(integration)}</strong>
              <span className="waDnBuFoot">BU {integration.business_unit_id} · {integration.settings?.display_phone_number || integration.settings?.phone_number_id}</span>
              <span className={`waDnBadge waDnBadge--${integration.has_credentials ? "configured" : "missing"}`}>
                {integration.has_credentials ? "Token Meta guardado" : "Sin token guardado"}
              </span>
            </button>)}
          </div>
          {!copySources.length && <p className="waDnHint" role="status">No hay otras BUs con un receptor guardado para copiar. Configura primero una BU de origen.</p>}
          {copySources.length > 0 && !visibleCopySources.length && <p className="waDnHint" role="status">No hay BUs de origen que coincidan con la búsqueda.</p>}
          {visibleCopySources.length > 60 && <p className="waDnHint">Se muestran 60 de {visibleCopySources.length}. Afina la búsqueda.</p>}
          {selectedCopySource && <section className="waDnCopySummary" aria-label="Resumen de la copia">
            <h3>{integrationBusinessUnitName(selectedCopySource)} → {selectedBusinessUnitName || `Business Unit ${selectedBusinessUnitId}`}</h3>
            <ul>
              <li>Receptor: {selectedCopySource.settings?.display_phone_number || selectedCopySource.settings?.phone_number_id}.</li>
              <li>Cuenta de WhatsApp Business, versión de API, estado de la integración y opciones de OCR, imágenes y PDF.</li>
              <li>Token Meta y verify token del webhook guardados. Los tokens se copian sin mostrarlos ni volver a introducirlos.</li>
            </ul>
            <p className="waDnCopyWarning"><strong>Se reemplazarán la configuración y los tokens del destino.</strong> Los cambios sin guardar del formulario de destino se descartarán al completar la copia.</p>
            {!selectedCopySource.has_credentials && <p className="waDnCopyWarning">Sin token guardado en el origen: el destino también quedará sin token Meta guardado.</p>}
            <p className="waDnHint">Se conservan las autorizaciones de remitentes del destino. Si es una integración nueva, la lista queda vacía.
              No se copian los remitentes del origen porque la combinación de receptor y remitente debe pertenecer a una sola BU.</p>
          </section>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="waDnButton" disabled={copying} onClick={closeCopyDialog}>Cancelar</button>
          <button type="button" className="waDnButton waDnButton--primary" disabled={copying || !selectedCopySource} onClick={copyIntegration}>
            <FiCopy aria-hidden="true" />{copying ? "Copiando…" : "Copiar configuración y tokens"}
          </button>
        </Modal.Footer>
      </Modal>
      <WhatsAppDeliveryNoteSetupGuide
        show={showSetupGuide}
        onHide={() => setShowSetupGuide(false)}
        businessUnitName={selectedBusinessUnitName}
      />
    </div>
  );
}
